import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { css, Global } from '@emotion/core'

import {
  barStyle,
  startBarStyle,
  betweenBarStyle,
  beginBarStyle,
  endBarStyle,
  dayUseBarStyle,
  barActiveStyle,
  nameStyle,
  barChangeStyle,
  startBarChangeStyle,
  betweenBarChangeStyle,
  beginBarChangeStyle,
  endBarChangeStyle,
  dayUseBarChangeStyle,
  endBarActiveStyle,
  beginBarActiveStyle,
  startBarActiveStyle,
  betweenBarActiveStyle,
  dayUseActiveBarStyle,
} from '@/components/pages/room-manager/_daily/_body/bar-layout'

import { SerializedStyles } from '@emotion/core'

// models
import { StatusType } from '@/models/room-manager/response/guest-room-assign-status'
import { DailyProps, DateType } from '@/components/pages/room-manager/_daily/daily-props'
import { Popover } from 'antd'

const DAILY_LIST_BLOCK_WIDTH = 111
// 表示開始日付以前（beginBarStyle）からの宿泊の場合、_beginDateと_assignCheckoutDaysの差分 + 0.5日分の表示になるのでnameWidthに加算する
const HALF_DAY_DIFF = 0.5
// 111 * 0.05：チェックアウト日のステータスバー（endBarStyle）のwidthが95%なので差分をnameWidthから差し引く値
const END_BAR_DIFF = 5.5
// 名前表示の余白部分を確保するためnameWidthから差し引く値
const MARGIN_VALUE = 15
// 表示開始日付以前（beginBarStyle）につける余白分を差し引く値
const BEGIN_PADDING_VALUE = 59.5

type StatusBarProps = {
  statusData: StatusType
  statusIndex: number
  date: DateType
  roomAssignActiveId: string
  roomNumber: string
  editGuestRooms: any
  beginDate: string
  endDate: string
  onClickStatusBar: DailyProps['onClickStatusBar']
  guestRoomId: string
  isHighlighted: boolean
  setHoveredCheckinId: (checkinId: string | null) => void
  setHoveredReservationId: (reservationId: string | null) => void
}

export const StatusBar: React.FC<StatusBarProps> = React.memo(
  ({
    statusData,
    statusIndex,
    date,
    roomAssignActiveId,
    roomNumber,
    editGuestRooms,
    beginDate,
    endDate,
    onClickStatusBar,
    guestRoomId,
    isHighlighted,
    setHoveredCheckinId,
    setHoveredReservationId,
  }) => {
    const { t } = useTranslation()
    const [isShowPopup, setIsShowPopup] = useState(false)
    const _assignCheckinDays = moment(statusData.assignCheckinDate)
    const _assignCheckoutDays = moment(statusData.assignCheckoutDate)
    const _days = moment(date.days)
    const _beginDate = moment(beginDate)
    const _endDate = moment(endDate)

    const nameWidth = (type: string) => {
      const diffAssignDays = _assignCheckoutDays.diff(_assignCheckinDays, 'day')
      const diffBeginDate = _assignCheckoutDays.diff(_beginDate, 'day') + HALF_DAY_DIFF

      const width = Math.min(diffAssignDays, diffBeginDate) * DAILY_LIST_BLOCK_WIDTH - MARGIN_VALUE - END_BAR_DIFF

      return type === 'beginBar' ? width - BEGIN_PADDING_VALUE : width
    }

    const onClickBar = e => {
      e.stopPropagation()
      onClickStatusBar(statusData, guestRoomId)
    }

    const onMouseEnter = () => {
      setHoveredCheckinId(statusData.checkinId ?? null)
      setHoveredReservationId(statusData.reservationId ?? null)
      setIsShowPopup(true)
    }

    const onMouseLeave = () => {
      setHoveredCheckinId(null)
      setHoveredReservationId(null)
      setIsShowPopup(false)
    }

    const statusColor = () => {
      switch (statusData.status) {
        case 'reservation':
          return '#FFFCDD'
        case 'checkin':
          return '#FEF3F0'
        case 'stay':
          return '#EFF9FF'
        case 'checkout':
          return '#F6FDF2'
        case 'preparation':
          return '#F2F2F2'
      }
    }

    const nameColor = () => {
      switch (statusData.status) {
        case 'reservation':
          return '#F2A40B'
        case 'checkin':
          return '#F47110'
        case 'stay':
          return '#3E85CC'
        case 'checkout':
          return '#7DC931'
        case 'preparation':
          return '#676767'
      }
    }

    // Return color depending on assignment status
    const _statusColor = () => {
      // Returns ActiveStyle if the reservation ID or assignment ID (if preparing) matches the active ID
      if (
        roomAssignActiveId &&
        (statusData.reservationId === roomAssignActiveId ||
          (statusData.status === 'preparation' && statusData.guestRoomAssignId === roomAssignActiveId))
      ) {
        if (
          /**
           * When the check-in date in the assignment data and the changed check-in date are different (when the check-in date is changed)
           * Or return an empty object if the room number has changed
           */
          statusData.assignCheckinDate !== statusData.changeAssignCheckinDate ||
          statusData.assignCheckoutDate !== statusData.changeAssignCheckoutDate ||
          !editGuestRooms.some(editGuestRoom => editGuestRoom.roomNumber === roomNumber)
        ) {
          return {}
        } else {
          // Returns the style if the assignment information has not changed
          return { background: nameColor() }
        }
      }

      // Otherwise return normal style
      return { background: statusColor(), color: nameColor() }
    }

    // バータイプに応じた基本スタイルを取得
    const getBaseBarStyle = (type: string) => {
      return type === 'endBar'
        ? endBarStyle
        : type === 'beginBar'
          ? beginBarStyle
          : type === 'startBar'
            ? startBarStyle
            : type === 'betweenBar'
              ? betweenBarStyle
              : type === 'dayUse'
                ? dayUseBarStyle
                : barStyle
    }

    // 選択状態に応じたスタイルを取得
    const getStateBarStyle = (type: string, isEditing: boolean) => {
      if (isEditing) {
        return type === 'endBar'
          ? endBarChangeStyle
          : type === 'beginBar'
            ? beginBarChangeStyle
            : type === 'startBar'
              ? startBarChangeStyle
              : type === 'betweenBar'
                ? betweenBarChangeStyle
                : type === 'dayUse'
                  ? dayUseBarChangeStyle
                  : barChangeStyle
      } else {
        return type === 'endBar'
          ? endBarActiveStyle
          : type === 'beginBar'
            ? beginBarActiveStyle
            : type === 'startBar'
              ? startBarActiveStyle
              : type === 'betweenBar'
                ? betweenBarActiveStyle
                : type === 'dayUse'
                  ? dayUseActiveBarStyle
                  : barActiveStyle
      }
    }

    // 選択状態かどうかを判定
    const isSelected = () => {
      return (
        roomAssignActiveId &&
        (statusData.reservationId === roomAssignActiveId ||
          (statusData.status === 'preparation' && statusData.guestRoomAssignId === roomAssignActiveId))
      )
    }

    // 編集中かどうかを判定
    const isEditing = () => {
      return (
        statusData.assignCheckinDate !== statusData.changeAssignCheckinDate ||
        statusData.assignCheckoutDate !== statusData.changeAssignCheckoutDate ||
        !editGuestRooms.some(editGuestRoom => editGuestRoom.roomNumber === roomNumber)
      )
    }

    // 連続予約の外周枠線スタイルを生成
    const getRelatedBorderStyle = (type: string) => {
      if (!isHighlighted) return {}

      const baseBorder = {
        borderTop: '2px solid #1890ff !important',
        borderBottom: '2px solid #1890ff !important',
      }

      // 左枠線：開始日、単日、日帰りのみ
      const leftBorder = type === 'startBar' || type === 'bar' || type === 'dayUse' ? { borderLeft: '2px solid #1890ff !important' } : {}

      // 右枠線：終了日、単日、日帰りのみ
      const rightBorder = type === 'endBar' || type === 'bar' || type === 'dayUse' ? { borderRight: '2px solid #1890ff !important' } : {}

      return { ...baseBorder, ...leftBorder, ...rightBorder }
    }

    // 最終的なバースタイルを生成
    const _barStyle = (type: string): SerializedStyles => {
      const relatedBorderStyle = getRelatedBorderStyle(type)

      if (isSelected()) {
        const stateStyle = getStateBarStyle(type, isEditing())
        return css(stateStyle, relatedBorderStyle)
      }

      return css(getBaseBarStyle(type), relatedBorderStyle)
    }

    const _bar = (type: string) => {
      return (
        <div
          key={`StatusBarCompoment_bar${statusIndex}_${Math.random() * 1000000}`}
          css={_barStyle(type)}
          style={_statusColor()}
          onClick={e => onClickBar(e)}
          // マウスホバー時に親コンポーネントに、ホバーした予約のcheckinIdとreservationIdを通知
          onMouseEnter={() => {
            setHoveredCheckinId(statusData.checkinId ?? null)
            setHoveredReservationId(statusData.reservationId ?? null)
          }}
          onMouseLeave={() => {
            setHoveredCheckinId(null)
            setHoveredReservationId(null)
          }}
        ></div>
      )
    }

    const _pinColor = () => {
      if (statusData.reservationId === roomAssignActiveId) return '#fff'

      switch (statusData.status) {
        case 'reservation':
          return '#f2a428'
        case 'checkin':
          return '#f47110'
        case 'stay':
          return '#3e85cc'
        case 'checkout':
          return '#7dc931'
        default:
          return '#fff'
      }
    }

    const _nameBar = (type: string) => {
      const name = statusData.userName || ''

      const popup =
        statusData.status !== 'preparation' ? (
          <div css={paxPopupStyle}>
            {<p>大人 : {paxForAdultText.join(' ') || '-'}</p>}
            {<p>子ども : {paxForChildText.join(' ') || '-'}</p>}
          </div>
        ) : null

      const isSelected = statusData.reservationId === roomAssignActiveId

      const bar = (
        <div
          key={`StatusBarCompoment_nameBar${statusIndex}`}
          css={_barStyle(type)}
          style={_statusColor()}
          onClick={e => onClickBar(e)}
          onMouseEnter={() => onMouseEnter()}
          onMouseLeave={() => onMouseLeave()}
        >
          {statusData.status !== 'preparation' ? (
            <>
              {!!statusData.isFixed && (
                <div css={{ marginRight: 4 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10">
                    <path
                      d="M9.258,2.293,8.5,3.049l.586.586L5.774,6.293,4.982,5.5l-.756.756L6.257,8.288,3,11.544v.749h.749L7.005,9.036l2.032,2.032.756-.756-.747-.747,2.622-3.35.576.576L13,6.035Z"
                      transform="translate(-3 -2.293)"
                      fill={_pinColor()}
                    />
                  </svg>
                </div>
              )}
              {statusData.hasUnpaidSales && (
                <div css={{ marginRight: 4 }}>
                  {isSelected ? (
                    <img src={require('@/static/images/room/settle_white.svg')} width={14} height={14} alt="unpaid" />
                  ) : (
                    <img src={require('@/static/images/room/settle_gray.svg')} width={14} height={14} alt="unpaid" />
                  )}
                </div>
              )}
              <div css={[nameStyle, { flex: `0 0 ${nameWidth(type)}px` }]}>{type === 'dayUse' ? name.charAt(0) : name}</div>
            </>
          ) : (
            <div css={nameStyle}>{statusData.preparationMemo || t('In preparation')}</div>
          )}
        </div>
      )

      return statusData.status !== 'preparation' ? (
        <>
          <Global
            styles={css({
              '.ant-popover-placement-bottom': { paddingTop: 0 },
              '.ant-popover-arrow': { display: 'none' },
            })}
          />
          <Popover placement="bottom" content={popup} visible={isShowPopup}>
            {bar}
          </Popover>
        </>
      ) : (
        bar
      )
    }

    const paxForAdultText = useMemo(() => {
      return [
        statusData.pax?.male ? t('cleaningManager.Male', { pax: statusData.pax?.male }) : undefined,
        statusData.pax?.female ? t('cleaningManager.Female', { pax: statusData.pax?.female }) : undefined,
      ].filter(t => t)
    }, [statusData])

    const paxForChildText = useMemo(() => {
      return [
        statusData.pax?.childA ? t('cleaningManager.ChildA', { pax: statusData.pax?.childA }) : undefined,
        statusData.pax?.childB ? t('cleaningManager.ChildB', { pax: statusData.pax?.childB }) : undefined,
        statusData.pax?.childC ? t('cleaningManager.ChildC', { pax: statusData.pax?.childC }) : undefined,
        statusData.pax?.childD ? t('cleaningManager.ChildD', { pax: statusData.pax?.childD }) : undefined,
        statusData.pax?.childE ? t('cleaningManager.ChildE', { pax: statusData.pax?.childE }) : undefined,
        statusData.pax?.childF ? t('cleaningManager.ChildF', { pax: statusData.pax?.childF }) : undefined,
        statusData.pax?.childOther ? t('cleaningManager.ChildOther', { pax: statusData.pax?.childOther }) : undefined,
      ].filter(t => t)
    }, [statusData])

    return (
      <React.Fragment key={`StatusBarCompoment${statusIndex}`}>
        {statusData.assignCheckinDate !== statusData.assignCheckoutDate
          ? moment(statusData.assignCheckinDate).add(1, 'd').isSame(moment(_assignCheckoutDays))
            ? /****************
               * 1 night 2 days
               ***************/
              // If the check-in date is before the start date, display endBar on the start date Otherwise, display the bar on the check-in date
              _assignCheckinDays.isBefore(_beginDate) && _beginDate.isSame(_days, 'day')
              ? _bar('endBar')
              : _assignCheckinDays.isSame(_days, 'day') && _nameBar('bar')
            : /****************
               * 2 nights or more
               ***************/
              [
                // If the check-in date is before the start date, at the position of the start date
                _assignCheckinDays.isBefore(_beginDate) &&
                  _beginDate.isSame(_days, 'day') &&
                  // endBar if checkout date, beginBar if not checkout date
                  (_beginDate.isSame(_assignCheckoutDays, 'day') ? _bar('endBar') : _nameBar('beginBar')),

                // startBar on check-in date
                _assignCheckinDays.isSame(_days, 'day') && _nameBar('startBar'),
                // Between is betweenBar(after check-in date && before check-out date)
                _assignCheckinDays.isBefore(_days, 'day') &&
                  moment(statusData.assignCheckoutDate).add(-1, 'd').isAfter(_days, 'day') &&
                  _bar('betweenBar'),
                // Checkout date is endBar
                statusData.assignCheckoutDate === date.days && _bar('endBar'),

                // If the check-out date is later than the end date and not the check-in date, display beginBar on the end date
                _assignCheckoutDays.isAfter(_endDate, 'day') &&
                  !_assignCheckinDays.isSame(_days, 'day') &&
                  _endDate.isSame(_days, 'day') &&
                  _bar('beginBar'),
              ]
          : /****************
             * day use
             ***************/
            _assignCheckinDays.isSame(_days, 'day') && _nameBar('dayUse')}
      </React.Fragment>
    )
  },
)

const paxPopupStyle = css({
  color: '#676767',
  lineHeight: 1.5,
  fontSize: 12,
  fontWeight: 'bold',
})
